import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, PackageOpen, ArrowLeft, ShoppingCart, Check, Heart, Ruler, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { zl, useCart } from "@/lib/shop";
import { shopApi, useShopAuth, errText } from "@/lib/shopAuth";
import ShopLayout from "./ShopLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ShopProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { customer, refresh } = useShopAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [gpsrOpen, setGpsrOpen] = useState(false);
  const [photo, setPhoto] = useState(0);
  const [favs, setFavs] = useState([]);

  useEffect(() => { setFavs(customer?.favorites || []); }, [customer]);

  const toggleFav = async () => {
    if (!customer) { toast.info("Zaloguj się, aby zapisywać ulubione"); navigate("/sklep/logowanie"); return; }
    setFavs((prev) => prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]);
    try { const { data: d } = await shopApi.post("/shop/auth/favorite", { product_id: p.id }); setFavs(d.favorites); refresh(); }
    catch (err) { toast.error(errText(err)); setFavs(customer?.favorites || []); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/shop/products/${id}`).then((r) => {
      setP(r.data);
      setColor(r.data.colors?.[0] || "");
      setSize(r.data.sizes?.[0] || "");
    }).catch(() => setP(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!p || !color) return;
    const fid = p.color_photos?.[color];
    if (fid) { const idx = p.photos?.indexOf(fid); if (idx >= 0) setPhoto(idx); }
  }, [color, p]);

  const addToCart = () => {
    if (p.colors?.length && !color) return toast.error("Wybierz kolor");
    if (p.sizes?.length && !size) return toast.error("Wybierz rozmiar");
    add({ product_id: p.id, name: p.name, photo: p.photos?.[0] || null, color, size, quantity: qty, price_grosz: p.price_grosz });
    toast.success("Dodano do koszyka");
  };

  const variantKey = `${color}||${size}`;
  const inStock = Array.isArray(p?.in_stock_variants) && p.in_stock_variants.includes(variantKey);
  const photoColor = {};
  Object.entries(p?.color_photos || {}).forEach(([c, fid]) => { photoColor[fid] = c; });
  const discountPct = p?.on_promo && p.promo_reference_grosz
    ? Math.round(((p.promo_reference_grosz - p.price_grosz) / p.promo_reference_grosz) * 100) : 0;

  const gpsrHasExtra = p?.gpsr && (p.gpsr.model_id || p.gpsr.phone || p.gpsr.eu_responsible || p.gpsr.warnings || p.gpsr.brand);

  return (
    <ShopLayout>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <button onClick={() => navigate("/sklep")} className="mb-6 flex items-center gap-1.5 text-sm text-neutral-400 hover:text-amber-400"><ArrowLeft className="h-4 w-4" />Powrót do sklepu</button>
        {loading ? <div className="grid place-items-center py-20 text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        : !p ? <div className="py-20 text-center text-neutral-500">Produkt nie znaleziony. <Link to="/sklep" className="text-amber-400 underline">Wróć do sklepu</Link></div>
        : (
          <div className="grid gap-10 lg:grid-cols-2">
            {/* Galeria */}
            <div>
              <div className="group relative aspect-square rounded-2xl bg-white overflow-hidden" data-testid="product-main-photo">
                {p.brand_logo && <img src={`${API}/files/${p.brand_logo}`} alt="" data-testid="product-brand-logo" className="absolute bottom-3 left-3 z-10 h-6 w-auto max-w-[19%] object-contain" />}
                {p.photos?.[photo]
                  ? <img src={`${API}/files/${p.photos[photo]}`} alt={p.name} className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-110" />
                  : <div className="grid h-full place-items-center text-neutral-300"><PackageOpen className="h-16 w-16" /></div>}
              </div>
              {p.photos?.length > 1 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {p.photos.map((ph, i) => (
                    <div key={ph} className="flex flex-col items-center gap-1">
                      <button onClick={() => setPhoto(i)} data-testid={`product-thumb-${i}`} className={`h-16 w-16 rounded-lg overflow-hidden border-2 ${photo === i ? "border-amber-400" : "border-transparent"}`}>
                        <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain bg-white p-1" />
                      </button>
                      {photoColor[ph] && <span className="max-w-16 truncate text-[10px] uppercase tracking-wide text-neutral-400">{photoColor[ph]}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kolumna zakupu + informacje */}
            <div>
              {p.category && <p className="text-xs uppercase tracking-[0.2em] text-amber-400">{p.category}</p>}
              <div className="mt-2 flex items-start justify-between gap-3">
                <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight" data-testid="product-name">{p.name}</h1>
                <button type="button" data-testid="product-fav-btn" onClick={toggleFav} aria-label="Dodaj do ulubionych"
                  className="shrink-0 grid h-11 w-11 place-items-center rounded-full border border-white/15 transition-transform hover:scale-110 active:scale-95 hover:border-amber-400/60">
                  <Heart className={`h-5 w-5 transition-colors ${favs.includes(p.id) ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
                </button>
              </div>
              {p.subtitle && <p className="mt-1 text-neutral-400">{p.subtitle}</p>}

              {/* Cena (Omnibus) */}
              <div className="mt-4 flex items-baseline gap-3 flex-wrap" data-testid="product-price">
                <span className="text-3xl font-extrabold">{zl(p.price_grosz)}</span>
                {p.on_promo && p.regular_grosz ? <span className="text-lg text-neutral-500 line-through" data-testid="product-regular-price">{zl(p.regular_grosz)}</span> : null}
                {p.on_promo && discountPct > 0 && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-xs font-bold text-white" data-testid="product-promo-badge">-{discountPct}%</span>}
              </div>
              {p.on_promo && (
                <p className="mt-1 text-xs text-neutral-400" data-testid="product-omnibus">
                  {p.promo_basis === "since_launch" ? "Najniższa cena od rozpoczęcia sprzedaży" : "Najniższa cena z 30 dni przed obniżką"}:{" "}
                  <span className="font-semibold text-neutral-200">{zl(p.promo_reference_grosz)}</span>
                </p>
              )}

              {/* Dostępność wybranego wariantu (bez liczb) */}
              <p className="mt-3 text-sm font-semibold" data-testid="product-availability">
                {inStock
                  ? <span className="text-emerald-400">● Dostępny</span>
                  : <span className="text-amber-400">Na zamówienie{p.lead_time ? ` · ${p.lead_time}` : " · dostępność do potwierdzenia"}</span>}
              </p>

              {/* Kolor */}
              {p.colors?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Kolor</p>
                  <div className="flex flex-wrap gap-2" data-testid="product-colors">
                    {p.colors.map((c) => (
                      <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)}
                        className={`rounded-full px-3 py-1.5 text-sm border ${color === c ? "border-amber-400 bg-amber-400/10 text-amber-300" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>{c}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Rozmiar */}
              {p.sizes?.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Rozmiar</p>
                  <div className="flex flex-wrap gap-2" data-testid="product-sizes">
                    {p.sizes.map((s) => (
                      <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                        className={`min-w-11 rounded-lg px-3 py-1.5 text-sm border ${size === s ? "border-amber-400 bg-amber-400/10 text-amber-300" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {(p.size_table?.length > 0 || p.size_chart_image) && (
                <button data-testid="size-spec-link" onClick={() => setSizeOpen(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-amber-400 hover:underline">
                  <Ruler className="h-4 w-4" />Tabela rozmiarów
                </button>
              )}

              {/* Sekcja zakupu */}
              <div className="mt-6 flex items-center gap-4">
                <div className="flex items-center rounded-lg border border-white/15">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 text-lg hover:text-amber-400">−</button>
                  <span data-testid="product-qty" className="w-10 text-center font-semibold">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="h-11 w-11 text-lg hover:text-amber-400">+</button>
                </div>
                <Button data-testid="add-to-cart-btn" onClick={addToCart} className="flex-1 h-12 bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-bold uppercase tracking-wider gap-2">
                  <ShoppingCart className="h-5 w-5" />Dodaj do koszyka
                </Button>
              </div>
              <p className="mt-3 flex items-center gap-2 text-xs text-neutral-500"><Check className="h-4 w-4 text-emerald-400" />Płatność online: BLIK, karta, przelew</p>

              <Dialog open={sizeOpen} onOpenChange={setSizeOpen}>
                <DialogContent className="max-w-lg" data-testid="size-spec-modal">
                  <DialogHeader><DialogTitle>Tabela rozmiarów (cm)</DialogTitle></DialogHeader>
                  {p.size_chart_image && <img src={p.size_chart_image.startsWith("http") ? p.size_chart_image : `${API}/files/${p.size_chart_image}`} alt="Diagram wymiarów A/B" className="mx-auto h-40 w-auto object-contain" />}
                  {p.size_table?.length > 0 && (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-muted-foreground"><th className="py-1">Rozmiar</th><th>Długość (A)</th><th>Szer. klatki (B)</th></tr></thead>
                      <tbody>
                        {p.size_table.map((r) => <tr key={r.size} className="border-t border-border"><td className="py-1 font-semibold">{r.size}</td><td>{r.length_a} cm</td><td>{r.chest_b} cm</td></tr>)}
                      </tbody>
                    </table>
                  )}
                </DialogContent>
              </Dialog>

              {/* Opis — pod sekcją zakupu */}
              {p.description && <p className="mt-8 text-neutral-300 leading-relaxed whitespace-pre-line" data-testid="product-description">{p.description}</p>}

              {/* Szczegóły produktu */}
              {(p.composition || p.gramatura || p.kroj || p.dekolt || p.szwy) && (
                <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-3">Szczegóły produktu</p>
                  <dl className="grid grid-cols-2 gap-y-2 text-sm">
                    {p.composition && <><dt className="text-neutral-500">Skład</dt><dd>{p.composition}</dd></>}
                    {p.gramatura && <><dt className="text-neutral-500">Gramatura</dt><dd>{p.gramatura}</dd></>}
                    {p.kroj && <><dt className="text-neutral-500">Krój</dt><dd>{p.kroj}</dd></>}
                    {p.dekolt && <><dt className="text-neutral-500">Dekolt</dt><dd>{p.dekolt}</dd></>}
                    {p.szwy && <><dt className="text-neutral-500">Szwy</dt><dd>{p.szwy}</dd></>}
                  </dl>
                </div>
              )}

              {/* Producent i bezpieczeństwo produktu — zwijane */}
              {p.gpsr && (p.gpsr.manufacturer || p.gpsr.address || p.gpsr.email) && (
                <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-5" data-testid="product-gpsr">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-3">Producent i bezpieczeństwo produktu</p>
                  <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
                    {p.gpsr.manufacturer && <><dt className="text-neutral-500">Producent</dt><dd>{p.gpsr.manufacturer}</dd></>}
                    {p.gpsr.address && <><dt className="text-neutral-500">Adres</dt><dd>{p.gpsr.address}</dd></>}
                    {p.gpsr.email && <><dt className="text-neutral-500">E-mail</dt><dd><a href={`mailto:${p.gpsr.email}`} className="text-amber-400 hover:underline">{p.gpsr.email}</a></dd></>}
                    {gpsrOpen && <>
                      {p.gpsr.brand && <><dt className="text-neutral-500">Marka</dt><dd>{p.gpsr.brand}</dd></>}
                      {p.gpsr.model_id && <><dt className="text-neutral-500">Model</dt><dd>{p.gpsr.model_id}</dd></>}
                      {p.gpsr.phone && <><dt className="text-neutral-500">Telefon</dt><dd>{p.gpsr.phone}</dd></>}
                      {p.gpsr.eu_responsible && <><dt className="text-neutral-500">Osoba odpowiedzialna w UE</dt><dd>{p.gpsr.eu_responsible}</dd></>}
                      {p.gpsr.warnings && <><dt className="text-neutral-500">Ostrzeżenia</dt><dd className="whitespace-pre-line">{p.gpsr.warnings}</dd></>}
                    </>}
                  </dl>
                  {gpsrHasExtra && (
                    <button data-testid="gpsr-toggle" onClick={() => setGpsrOpen((v) => !v)} className="mt-3 inline-flex items-center gap-1 text-sm text-amber-400 hover:underline">
                      {gpsrOpen ? <>Zwiń<ChevronUp className="h-4 w-4" /></> : <>Zobacz więcej<ChevronDown className="h-4 w-4" /></>}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ShopLayout>
  );
}
